import type { Route, StopTimeData } from '@/@types/gtfs-ressource';

export interface DepartureTime {
  timestamp: number;
  realTime: boolean;
}

/** One direction of one route serving the stop: a route_id + trip_headsign pair. */
interface DepartureRow {
  rowId: string;
  routeId: string;
  tripHeadsign: string;
  /** Next departures, soonest first. Empty means the direction is done for today. */
  times: DepartureTime[];
}

/** Every direction of a single route, as one departures card. */
export interface DepartureGroup {
  routeId: string;
  /** Absent when the stop_times reference a route missing from the GTFS payload. */
  route?: Route;
  rows: DepartureRow[];
}

/**
 * Group a stop's stop_times into per-route departure cards, one row per
 * direction. Lighter sibling of the stop view's rows/routeGroups computed:
 * the home sheet shows plain next departures, without tags, alerts, or
 * trip-update refinements — the stop view stays the reference.
 */
export function buildStopDepartures(
  stopTimes: StopTimeData[],
  stopIds: string[],
  routes: { [routeId: string]: Route },
  nowTs: number,
  maxTimesPerRow: number,
): DepartureGroup[] {
  const rowsById: { [rowId: string]: DepartureRow } = {};

  stopTimes
    .filter(st => !st.last_stop && stopIds.indexOf(st.stop_id) >= 0)
    .forEach(st => {
      // Separator prevents collisions between (route, headsign) pairs whose
      // concatenations overlap — GTFS route_ids are often numeric prefixes.
      const rowId = `${st.route_id}|${st.trip_headsign}`;
      if (!rowsById[rowId]) {
        rowsById[rowId] = {
          rowId,
          routeId: st.route_id,
          tripHeadsign: st.trip_headsign,
          times: [],
        };
      }

      const timestamp = st.estimated_departure_ts || st.departure_ts;
      if (timestamp >= nowTs) {
        rowsById[rowId].times.push({ timestamp, realTime: !!st.estimated_departure_ts });
      }
    });

  const rows = Object.values(rowsById);
  rows.forEach(row => {
    row.times.sort((a, b) => a.timestamp - b.timestamp);
    row.times = row.times.slice(0, maxTimesPerRow);
  });

  // Soonest departure first; directions done for today sink to the bottom.
  rows.sort((a, b) => {
    if (a.times.length && b.times.length) return a.times[0].timestamp - b.times[0].timestamp;
    return a.times.length ? -1 : 1;
  });

  const groups: DepartureGroup[] = [];
  const groupsByRouteId: { [routeId: string]: DepartureGroup } = {};
  rows.forEach(row => {
    let group = groupsByRouteId[row.routeId];
    if (!group) {
      group = { routeId: row.routeId, route: routes[row.routeId], rows: [] };
      groupsByRouteId[row.routeId] = group;
      groups.push(group);
    }
    group.rows.push(row);
  });

  return groups;
}
